import { ArrowLeftIcon, EnvelopeIcon, PhoneIcon } from '@heroicons/react/24/outline';
import { Link, useParams } from 'react-router-dom';

import { Card, Badge, Avatar, Button, Spinner } from '@realvisor/ui';
import { useContact } from '@/hooks';
import { usePermissions } from '@/hooks/usePermissions';
import { useSip } from '@/contexts/SipContext';
import { getLeadAgeColor, formatLeadAge } from '@/utils/leadAgeColor';
import { maskValue } from '@/utils/maskValue';

function formatDate(dateString: string | null | undefined): string {
  if (!dateString) return '-';
  try {
    const date = new Date(dateString);
    if (Number.isNaN(date.getTime())) return '-';
    return date.toLocaleDateString('cs-CZ', {
      day: 'numeric',
      month: 'long',
      year: 'numeric',
    });
  } catch (error) {
    console.error('Invalid date format:', dateString, error);
    return '-';
  }
}

function formatValue(value: number | null | undefined): string {
  if (!value) return '-';
  if (value >= 1000000) return `${(value / 1000000).toFixed(1)}M Kč`;
  if (value >= 1000) return `${(value / 1000).toFixed(0)}K Kč`;
  return `${value.toLocaleString()} Kč`;
}

function getCompanyName(company: unknown): string | null {
  if (!company) return null;
  if (typeof company === 'string') return company;
  if (typeof company === 'object' && company !== null && 'name' in company) {
    return (company as { name: string }).name;
  }
  return null;
}

function getCompanyId(company: unknown): string | null {
  if (!company) return null;
  if (typeof company === 'object' && company !== null && 'id' in company) {
    return (company as { id: string }).id;
  }
  return null;
}

function sourceLabel(source: string | null | undefined): string {
  const map: Record<string, string> = {
    widget: 'Widget / formulář',
    valuo_form: 'Ocenění nemovitosti',
    prescoring: 'Prescoring hypotéky',
    hypokalkulacka: 'Hypokalkulačka',
    manual: 'Manuální zadání',
    import: 'Import',
  };
  return source ? (map[source] || source) : '-';
}

const CONTACT_TYPE_LABELS: Record<string, string> = {
  lead: 'Potenciální klient',
  client: 'Klient',
  partner: 'Partner',
  supplier: 'Dodavatel',
  referral: 'Doporučení',
  other: 'Ostatní',
};

const CONTACT_SCOPE_LABELS: Record<string, string> = {
  b2b: 'B2B',
  b2c: 'B2C',
};

export default function ContactDetail() {
  const { id } = useParams<{ id: string }>();
  const { data: contact, isLoading, error } = useContact(id || '');
  const sip = useSip();
  const { can } = usePermissions();
  const canSeeAllContacts = can('contacts:all');

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Spinner size="lg" />
      </div>
    );
  }

  if (error || !contact) {
    return (
      <Card padding="lg">
        <div className="text-center py-8">
          <p className="text-red-500">Kontakt nebyl nalezen</p>
          <Link to="/contacts" className="mt-4 inline-block text-primary-600 hover:underline">
            Zpět na seznam
          </Link>
        </div>
      </Card>
    );
  }

  const fullName = `${contact.firstName} ${contact.lastName || ''}`.trim();
  const companyName = getCompanyName(contact.company);
  const companyId = getCompanyId(contact.company);

  // Meta fields
  const meta = contact.meta || {};
  const ico = meta['ico'] as string | undefined;
  const dic = meta['dic'] as string | undefined;
  const metaCompanyName = meta['companyName'] as string | undefined;
  const legalForm = meta['legalForm'] as string | undefined;
  const category = meta['category'] as string | undefined;
  const properties = meta['properties'] as { parcelsCount?: number; totalArea?: number; locations?: string[] } | undefined;

  // Detect if contact came from a specific form source
  const hasSourceData = !!contact.source;
  const hasPersonalData = !!(contact.personalEmail || contact.personalPhone || contact.department || contact.linkedinUrl || contact.websiteUrl);
  const hasEnrichmentData = !!(ico || dic || metaCompanyName || legalForm || category || properties);
  const hasConsentData = !!(contact.marketingEmailConsent || contact.marketingPhoneConsent || contact.marketingSmsConsent || contact.consentGivenAt || contact.doNotContact);

  return (
    <div className={`space-y-6${!canSeeAllContacts ? ' select-none' : ''}`} onCopy={!canSeeAllContacts ? (e) => e.preventDefault() : undefined}>
      {/* Header */}
      <div className="flex items-center gap-4">
        <Link
          to="/contacts"
          className="flex items-center justify-center rounded-lg border border-gray-200 p-2 hover:bg-gray-50"
        >
          <ArrowLeftIcon className="h-5 w-5 text-gray-500" />
        </Link>
        <Avatar name={fullName} size="lg" />
        <div className="flex-1">
          <div className="flex items-center gap-2 flex-wrap">
            <h1 className="text-2xl font-bold text-gray-900">{fullName}</h1>
            {contact.temperature === 'hot' && (
              <span className="inline-flex items-center rounded-full bg-red-100 px-2.5 py-0.5 text-sm font-medium text-red-700">Horky</span>
            )}
            {contact.temperature === 'warm' && (
              <span className="inline-flex items-center rounded-full bg-orange-100 px-2.5 py-0.5 text-sm font-medium text-orange-700">Teply</span>
            )}
            {(!contact.temperature || contact.temperature === 'cold') && (
              <span className="inline-flex items-center rounded-full bg-blue-50 px-2.5 py-0.5 text-sm font-medium text-blue-600">Studeny</span>
            )}
            {contact.doNotContact && (
              <span className="inline-flex items-center rounded-full bg-red-50 px-2.5 py-0.5 text-sm font-medium text-red-600">Nekontaktovat</span>
            )}
            {contact.importBatchId && (
              <span className="inline-flex items-center rounded-full bg-gray-100 px-2.5 py-0.5 text-xs font-medium text-gray-500">{contact.importBatchId}</span>
            )}
          </div>
          <p className="text-sm text-gray-500">
            {contact._count?.leads || 0} leadů • Vytvořeno {formatDate(contact.createdAt)}
            {contact.lastContactedAt && ` • Poslední kontakt ${formatDate(contact.lastContactedAt)}`}
          </p>
        </div>
        <div className="flex gap-2">
          {contact.email && canSeeAllContacts && (
            <a href={`mailto:${contact.email}`}>
              <Button variant="outline" size="sm">
                <EnvelopeIcon className="mr-2 h-4 w-4" />
                Email
              </Button>
            </a>
          )}
          {contact.phone && (
            sip.isConfigured ? (
              <Button
                variant="outline"
                size="sm"
                onClick={() => sip.makeCall(contact.phone!, contact.id)}
                disabled={sip.callState !== 'idle'}
              >
                <PhoneIcon className="mr-2 h-4 w-4" />
                {sip.callState !== 'idle' ? 'Hovor probíhá…' : 'Volat'}
              </Button>
            ) : canSeeAllContacts ? (
              <a href={`tel:${contact.phone}`}>
                <Button variant="outline" size="sm">
                  <PhoneIcon className="mr-2 h-4 w-4" />
                  Volat
                </Button>
              </a>
            ) : null
          )}
        </div>
      </div>

      {/* Content */}
      <div className="grid gap-6 lg:grid-cols-3">
        {/* Main info */}
        <div className="lg:col-span-2 space-y-6">

          {/* Kontaktní údaje */}
          <Card>
            <div className="p-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-4">Kontaktní údaje</h2>
              <div className="grid gap-4 sm:grid-cols-2">
                <div>
                  <span className="text-sm text-gray-500">Email</span>
                  <p className="font-medium">{maskValue(contact.email, canSeeAllContacts)}</p>
                </div>
                <div>
                  <span className="text-sm text-gray-500">Telefon</span>
                  <p className="font-medium">{maskValue(contact.phone, canSeeAllContacts)}</p>
                </div>
                {contact.personalEmail && (
                  <div>
                    <span className="text-sm text-gray-500">Osobní email</span>
                    <p className="font-medium">{maskValue(contact.personalEmail, canSeeAllContacts)}</p>
                  </div>
                )}
                {contact.personalPhone && (
                  <div>
                    <span className="text-sm text-gray-500">Osobní telefon</span>
                    <p className="font-medium">{maskValue(contact.personalPhone, canSeeAllContacts)}</p>
                  </div>
                )}
                {companyName && (
                  <div>
                    <span className="text-sm text-gray-500">Společnost</span>
                    {companyId ? (
                      <Link to={`/companies/${companyId}`} className="font-medium text-primary-600 hover:underline block">
                        {companyName}
                      </Link>
                    ) : (
                      <p className="font-medium">{companyName}</p>
                    )}
                  </div>
                )}
                {contact.position && (
                  <div>
                    <span className="text-sm text-gray-500">Pozice</span>
                    <p className="font-medium">{contact.position}</p>
                  </div>
                )}
                {contact.department && (
                  <div>
                    <span className="text-sm text-gray-500">Oddělení</span>
                    <p className="font-medium">{contact.department}</p>
                  </div>
                )}
                {contact.city && (
                  <div>
                    <span className="text-sm text-gray-500">Město</span>
                    <p className="font-medium">{contact.city}</p>
                  </div>
                )}
                {contact.country && (
                  <div>
                    <span className="text-sm text-gray-500">Země</span>
                    <p className="font-medium">{contact.country}</p>
                  </div>
                )}
                {contact.linkedinUrl && (
                  <div>
                    <span className="text-sm text-gray-500">LinkedIn</span>
                    <a href={contact.linkedinUrl} target="_blank" rel="noopener noreferrer" className="font-medium text-primary-600 hover:underline block truncate">
                      {contact.linkedinUrl}
                    </a>
                  </div>
                )}
                {contact.websiteUrl && (
                  <div>
                    <span className="text-sm text-gray-500">Web</span>
                    <a href={contact.websiteUrl} target="_blank" rel="noopener noreferrer" className="font-medium text-primary-600 hover:underline block truncate">
                      {contact.websiteUrl}
                    </a>
                  </div>
                )}
                {contact.contactType && (
                  <div>
                    <span className="text-sm text-gray-500">Typ kontaktu</span>
                    <p className="font-medium">{CONTACT_TYPE_LABELS[contact.contactType] || contact.contactType}</p>
                  </div>
                )}
                {contact.contactScope && (
                  <div>
                    <span className="text-sm text-gray-500">Segment</span>
                    <p className="font-medium">{CONTACT_SCOPE_LABELS[contact.contactScope] || contact.contactScope}</p>
                  </div>
                )}
                {contact.score != null && contact.score > 0 && (
                  <div>
                    <span className="text-sm text-gray-500">Skóre</span>
                    <p className="font-medium">{contact.score}</p>
                  </div>
                )}
                <div>
                  <span className="text-sm text-gray-500">Vytvořeno</span>
                  <p className="font-medium">{formatDate(contact.createdAt)}</p>
                </div>
              </div>
            </div>
          </Card>

          {/* Obohacení / IČO data */}
          {hasEnrichmentData && (
            <Card>
              <div className="p-6">
                <h2 className="text-lg font-semibold text-gray-900 mb-4">Firemní data</h2>
                <div className="grid gap-4 sm:grid-cols-2">
                  {ico && (
                    <div>
                      <span className="text-sm text-gray-500">IČO</span>
                      <p className="font-medium">{ico}</p>
                    </div>
                  )}
                  {dic && (
                    <div>
                      <span className="text-sm text-gray-500">DIČ</span>
                      <p className="font-medium">{dic}</p>
                    </div>
                  )}
                  {metaCompanyName && (
                    <div>
                      <span className="text-sm text-gray-500">Název firmy</span>
                      <p className="font-medium">{metaCompanyName}</p>
                    </div>
                  )}
                  {legalForm && (
                    <div>
                      <span className="text-sm text-gray-500">Právní forma</span>
                      <p className="font-medium">{legalForm}</p>
                    </div>
                  )}
                  {category && (
                    <div>
                      <span className="text-sm text-gray-500">Kategorie</span>
                      <p className="font-medium">{category}</p>
                    </div>
                  )}
                  {properties && (
                    <div className="sm:col-span-2">
                      <span className="text-sm text-gray-500">Nemovitosti</span>
                      <p className="font-medium">
                        {properties.parcelsCount} parcel
                        {properties.totalArea ? ` • ${(properties.totalArea / 10000).toFixed(1)} ha` : ''}
                        {properties.locations?.length ? ` • ${properties.locations.join(', ')}` : ''}
                      </p>
                    </div>
                  )}
                </div>
              </div>
            </Card>
          )}

          {/* Zdroj kontaktu */}
          {hasSourceData && (
            <Card>
              <div className="p-6">
                <h2 className="text-lg font-semibold text-gray-900 mb-4">Zdroj kontaktu</h2>
                <div className="grid gap-4 sm:grid-cols-2">
                  <div>
                    <span className="text-sm text-gray-500">Zdroj</span>
                    <p className="font-medium">{sourceLabel(contact.source)}</p>
                  </div>
                  {contact.sourceDetail && (
                    <div>
                      <span className="text-sm text-gray-500">Detail zdroje</span>
                      <p className="font-medium">{contact.sourceDetail}</p>
                    </div>
                  )}
                  {contact.sourceUrl && (
                    <div className="sm:col-span-2">
                      <span className="text-sm text-gray-500">URL zdroje</span>
                      <p className="font-medium text-sm break-all text-gray-700">{contact.sourceUrl}</p>
                    </div>
                  )}
                </div>
              </div>
            </Card>
          )}

          {/* Leady */}
          <Card>
            <div className="p-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-4">Leady</h2>
              {(!contact.leads || contact.leads.length === 0) ? (
                <p className="text-center py-4 text-gray-500">Žádné leady</p>
              ) : (
                <div className="space-y-2">
                  {contact.leads.map((lead) => {
                    const ageInfo = getLeadAgeColor(lead.createdAt);
                    return (
                    <Link
                      key={lead.id}
                      to={`/leads/${lead.id}`}
                      className="block rounded-lg border border-gray-200 p-3 hover:bg-gray-50"
                      style={{ backgroundColor: ageInfo.backgroundColor }}
                    >
                      <div className="flex items-start justify-between gap-3">
                        <div className="min-w-0">
                          <p className="font-medium text-gray-900 truncate">
                            {lead.project?.name || 'Bez projektu'}
                          </p>
                          <p className="text-sm text-gray-500">
                            {lead.currentStage?.name || 'Bez fáze'} • <span style={{ color: ageInfo.isUrgent ? '#dc2626' : '#6b7280' }}>{formatLeadAge(lead.createdAt)}</span>
                          </p>
                        </div>
                        <div className="text-right">
                          <p className="font-medium text-gray-900">{formatValue(lead.expectedValue)}</p>
                          <div className="mt-1 flex justify-end gap-2">
                            {lead.currentStage?.color && (
                              <span
                                className="inline-flex rounded-full px-2.5 py-0.5 text-xs font-medium"
                                style={{
                                  backgroundColor: `${lead.currentStage.color}20`,
                                  color: lead.currentStage.color,
                                }}
                              >
                                {lead.currentStage.name}
                              </span>
                            )}
                            {lead.status && (
                              <Badge variant="default">{lead.status}</Badge>
                            )}
                          </div>
                        </div>
                      </div>
                    </Link>
                  );})}

                  {contact._count?.leads && contact._count.leads > contact.leads.length && (
                    <p className="text-xs text-gray-500 pt-2">
                      Zobrazeno {contact.leads.length} z {contact._count.leads} leadů
                    </p>
                  )}
                </div>
              )}
            </div>
          </Card>
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          {contact.tags && contact.tags.length > 0 && (
            <Card>
              <div className="p-6">
                <h2 className="text-lg font-semibold text-gray-900 mb-4">Tagy</h2>
                <div className="flex flex-wrap gap-2">
                  {contact.tags.map((tag) => (
                    <Badge key={tag} variant="default">{tag}</Badge>
                  ))}
                </div>
              </div>
            </Card>
          )}

          <Card>
            <div className="p-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-4">Poznámky</h2>
              {contact.notes ? (
                <p className="text-sm text-gray-700 whitespace-pre-wrap">{contact.notes}</p>
              ) : (
                <p className="text-sm text-gray-500">Zatím žádné poznámky</p>
              )}
            </div>
          </Card>

          {/* GDPR / Souhlasy */}
          {hasConsentData && (
            <Card>
              <div className="p-6">
                <h2 className="text-lg font-semibold text-gray-900 mb-4">GDPR & Souhlasy</h2>
                <div className="space-y-2 text-sm">
                  <div className="flex items-center justify-between">
                    <span className="text-gray-500">Email marketing</span>
                    <span className={contact.marketingEmailConsent ? 'text-green-600 font-medium' : 'text-gray-400'}>
                      {contact.marketingEmailConsent ? '✓ Souhlas' : '✗ Nesouhlas'}
                    </span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-gray-500">Telefonní marketing</span>
                    <span className={contact.marketingPhoneConsent ? 'text-green-600 font-medium' : 'text-gray-400'}>
                      {contact.marketingPhoneConsent ? '✓ Souhlas' : '✗ Nesouhlas'}
                    </span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-gray-500">SMS marketing</span>
                    <span className={contact.marketingSmsConsent ? 'text-green-600 font-medium' : 'text-gray-400'}>
                      {contact.marketingSmsConsent ? '✓ Souhlas' : '✗ Nesouhlas'}
                    </span>
                  </div>
                  {contact.doNotContact && (
                    <div className="mt-2 rounded-md bg-red-50 p-2 text-red-700 font-medium">
                      Nekontaktovat
                      {contact.doNotContactReason && <p className="text-xs font-normal mt-1">{contact.doNotContactReason}</p>}
                    </div>
                  )}
                  {contact.consentGivenAt && (
                    <p className="text-xs text-gray-400 mt-2">Souhlas udělen: {formatDate(contact.consentGivenAt)}</p>
                  )}
                </div>
              </div>
            </Card>
          )}
        </div>
      </div>
    </div>
  );
}
